import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ColumnHeads from '../components/drift/ColumnHeads';
import DeleteSelected from '../components/drift/DeleteSelected';
import PlanActions from '../components/drift/PlanActions';
import JsonEditor, { jsonText } from '../components/JsonEditor';
import PageHeader from '../components/PageHeader';
import ShowCommand from '../components/ShowCommand';
import { OPEN, STATES, findingIdsOf, options, stateClass, stateText, stepsText } from '../drift/actionModel';
import { whenText } from '../drift/findingModel';
import { useTableView } from '../drift/useTableView';
import { usePlans } from '../drift/usePlans';
import { useV3Account } from '../useV3Account';

const ROUTE = 'v3/actions';
const SPAN = 10;                                   // the caret, the checkbox and the eight columns

const COLUMNS = [
  { key: 'device', label: 'Device', text: (p) => p.device },
  { key: 'program', label: 'Program', text: (p) => `${p.subjectName || p.subject}${findingIdsOf(p).length > 1 ? ` (${findingIdsOf(p).length} findings)` : ''}` },
  { key: 'action', label: 'Action', text: (p) => p.action, options: true },
  { key: 'state', label: 'State', text: (p) => stateText(p.state), sort: (p) => STATES.indexOf(p.state), options: true, order: STATES.map(stateText) },
  { key: 'steps', label: 'Steps', text: (p) => stepsText(p) },
  { key: 'by', label: 'Requested by', text: (p) => String(p.requestedBy || '').replace(/^user:/, '').replace(/@.*$/, '') },
  { key: 'attempts', label: 'Attempt', text: (p) => String(p.attempts || 1), sort: (p) => p.attempts || 1 },
  { key: 'updated', label: 'Updated', text: (p) => whenText(p.updatedUtc), sort: (p) => p.updatedUtc },
];

// The plan opened in place: its state, the jobs queued for it, and the buttons for what is not queued yet.
function PlanView({ plan, account, plans }) {
  const step = (kind) => (plan.steps || {})[kind];
  return (
    <div className="finding-view" role="group" aria-label={`${plan.subjectName || plan.subject} plan`}>
      <dl className="kv">
        <dt>Plan</dt><dd><PlanActions plan={plan} busy={plans.busy === plan.id} onApprove={(kind) => plans.approve(plan, kind)} /></dd>
        <dt>What</dt><dd>{plan.action} {plan.subjectName || plan.subject} on {plan.device}</dd>
        {['test', 'apply'].map((kind) => step(kind) && (
          <React.Fragment key={kind}>
            <dt>{kind === 'test' ? 'Test' : 'Apply'}</dt>
            <dd className="long">job <span className="mono">{step(kind).jobId}</span>, queued {whenText(step(kind).queuedUtc)} by {String(step(kind).by || '').replace(/^user:/, '')}</dd>
          </React.Fragment>
        ))}
        <dt>{findingIdsOf(plan).length > 1 ? 'Findings' : 'Finding'}</dt>
        <dd className="mono long">{findingIdsOf(plan).map((id) => <div key={id}>{id}</div>)}</dd>
        <dt>Id</dt><dd className="mono long">{plan.id}</dd>
      </dl>
      <div className="field">
        <div className="label">Raw plan</div>
        <JsonEditor value={jsonText(plan)} readOnly label="Raw plan" maxHeight={420} />
      </div>
      <div className="editor-actions">
        <Link className="btn btn-sm" to={`/v3/findings?account=${encodeURIComponent(account)}`}>Findings</Link>
        <ShowCommand route="v3/actions/approve" method="POST" params={{ account, id: plan.id, kind: 'apply' }} label="PowerShell" />
      </div>
    </div>
  );
}

// The decisions made about findings: a plan for each action someone chose, and the id of the job queued for it. Queuing a job is where a plan
// ends: nothing here reads a job back or changes a plan afterwards. Whether the change worked shows on the Findings page (the finding resolves).
export default function ActionsPage() {
  const { accounts, account, choose, loading: accountsLoading, error: accountsError } = useV3Account();
  const code = account && account.code;
  const plans = usePlans(code);
  const [show, setShow] = useState('all');
  const [open, setOpen] = useState('');
  const [picked, setPicked] = useState({});                   // plan id -> true: the plans ticked for a bulk Test or Apply

  const base = useMemo(() => plans.plans.filter((p) => (show === 'all' ? true : show === 'open' ? OPEN.includes(p.state) : p.state === show)), [plans.plans, show]);
  const view = useTableView(base, COLUMNS);
  const rows = open ? base.filter((p) => p.id === open) : view.rows;
  const chosen = view.rows.filter((p) => picked[p.id]);                             // only what is shown is acted on, whatever else was ticked
  const testable = chosen.filter((p) => options(p).test);                           // the part of it that has no test queued yet
  const appliable = chosen.filter((p) => options(p).apply);                         // ... and no apply
  const allChosen = view.rows.length > 0 && chosen.length === view.rows.length;
  const toggleAll = () => setPicked((prev) => ({ ...prev, ...Object.fromEntries(view.rows.map((p) => [p.id, !allChosen])) }));
  const bulk = async (kind) => { await plans.approveMany(kind === 'test' ? testable : appliable, kind); setPicked({}); };
  const removeChosen = async () => { await plans.removeMany(chosen); setPicked({}); setOpen(''); };

  return (
    <>
      <PageHeader
        title="Actions"
        bear="bongbong"
        subtitle="What was decided about findings: each plan and the job queued for it. Create them from the Findings page (tick findings, then Create actions) or from the Dashboard (tick subjects), then test or apply one or many here. A queued job is the end of a plan: nothing here checks how it went."
        actions={<ShowCommand route={ROUTE} params={{ account: code }} disabled={!code} />}
      />

      {accountsError && <div className="field-error" role="alert">{accountsError}</div>}
      {plans.message.notice && <div className="notice" role="status">{plans.message.notice}</div>}
      {plans.message.problem && <div className="field-error" role="alert">{plans.message.problem}</div>}

      <div className="card">
        <div className="filter-bar toolbar">
          <div className="field inline">
            <label htmlFor="v3-account">Account</label>
            <select id="v3-account" value={code || ''} disabled={accountsLoading || accounts.length === 0} onChange={(e) => { choose(e.target.value); setOpen(''); }}>
              {accounts.map((a) => <option key={a.code} value={a.code}>{`${a.code} - ${a.name}`}</option>)}
            </select>
          </div>
          <div className="field inline">
            <label htmlFor="f-show">Show</label>
            <select id="f-show" value={show} onChange={(e) => { setShow(e.target.value); setOpen(''); }}>
              <option value="all">All</option>
              <option value="open">Waiting</option>
              {STATES.map((s) => <option key={s} value={s}>{stateText(s)}</option>)}
            </select>
          </div>
          <div className="bulk-bar" role="group" aria-label="Selected plans">
            <span className="sub">{chosen.length} selected of {view.rows.length} shown</span>
            <button type="button" className="btn btn-sm" disabled={testable.length === 0 || !!plans.busy} onClick={() => bulk('test')}>Test selected ({testable.length})</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={appliable.length === 0 || !!plans.busy} onClick={() => bulk('apply')}>Apply selected ({appliable.length})</button>
            <DeleteSelected count={chosen.length} busy={!!plans.busy} noun="plan" onDelete={removeChosen} />
          </div>
        </div>

        {plans.loading && <div className="empty">Loading...</div>}
        {plans.error && <div className="card-body"><div className="field-error" role="alert">{plans.error}</div></div>}
        {!plans.loading && !plans.error && base.length === 0 && (
          <div className="empty">{plans.plans.length === 0 ? 'No plans yet. Tick findings on the Findings page and choose "Create actions", or tick subjects on the Dashboard.' : 'No plans in this state.'}</div>
        )}
        {base.length > 0 && (
          <div className={`table-wrap${open ? ' has-open' : ''}`}>
            <table className="open-rows" aria-label="Action plans">
              <thead><ColumnHeads view={view} lead={1} select={{ checked: allChosen, indeterminate: chosen.length > 0 && !allChosen, onChange: toggleAll, label: 'Select all shown' }} /></thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={SPAN} className="empty">No plans match the column filters. <button type="button" className="btn btn-sm" onClick={view.clear}>Clear filters</button></td></tr>
                )}
                {rows.map((p) => {
                  const isOpen = open === p.id;
                  const toggle = () => setOpen(isOpen ? '' : p.id);
                  return (
                    <React.Fragment key={p.id}>
                      <tr className={`row${isOpen ? ' open' : ''}`} data-testid={`plan-${p.id}`} aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                        <td><span className="caret" aria-hidden="true">&#9656;</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Select ${p.subjectName || p.subject} on ${p.device}`} checked={!!picked[p.id]}
                            onChange={(e) => setPicked((prev) => ({ ...prev, [p.id]: e.target.checked }))} />
                        </td>
                        <td className="strong">{p.device}</td>
                        <td>{p.subjectName || p.subject}{findingIdsOf(p).length > 1 && <span className="sub"> ({findingIdsOf(p).length} findings)</span>}</td>
                        <td>{p.action}</td>
                        <td><span className={`pill ${stateClass(p.state)}`}>{stateText(p.state)}</span></td>
                        <td className="wrap">{stepsText(p)}</td>
                        <td>{String(p.requestedBy || '').replace(/^user:/, '').replace(/@.*$/, '')}</td>
                        <td>{p.attempts || 1}</td>
                        <td className="sub nowrap">{whenText(p.updatedUtc)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="detail" data-testid={`detail-${p.id}`}>
                          <td colSpan={SPAN}><PlanView plan={p} account={code} plans={plans} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
